import { useState } from "react";
import {
  ShoppingCart, UploadCloud, Loader2, Sparkles, X, Check, Trash2, FileDown, CheckCircle2,
} from "lucide-react";
import {
  useCompras, useScanCompra, useCreateCompra, useUpdateCompra, useDeleteCompra,
  downloadCompra, type CompraOcrCampos, type ScanResult,
} from "../api/purchases";
import { useIssuers } from "@modules/billing/api/issuers";

const eur = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);

export function ComprasPage() {
  const [estado, setEstado] = useState("");
  const { data, isLoading } = useCompras(estado);
  const scan = useScanCompra();
  const marcar = useUpdateCompra();
  const eliminar = useDeleteCompra();
  const [draft, setDraft] = useState<{ scan: ScanResult; campos: CompraOcrCampos } | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  function onFile(file: File | undefined) {
    if (!file) return;
    setScanError(null);
    scan.mutate(file, {
      onSuccess: (r) => {
        const campos: CompraOcrCampos = r.ocr.campos ?? {
          proveedor_nombre: null, proveedor_cif: null, numero_factura: null,
          fecha: null, vencimiento: null, base: null, iva_importe: null, total: null,
        };
        if (!r.ocr.ok && r.ocr.error) setScanError(r.ocr.error);
        setDraft({ scan: r, campos });
      },
      onError: () => setScanError("No se pudo procesar el archivo."),
    });
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0 overflow-auto">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <ShoppingCart size={26} className="text-brand-700" /> Compras
          </h1>
          <p className="text-slate-500 mt-1">
            Facturas de proveedor. Sube el PDF o una foto y la <strong>IA extrae los datos</strong> — tú solo revisas.
          </p>
        </div>
        {data && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-right">
            <div className="text-xs font-bold uppercase text-amber-700">Pendiente de pago</div>
            <div className="text-xl font-black text-amber-800">{eur(data.pendiente_total)}</div>
          </div>
        )}
      </header>

      {/* Dropzone OCR */}
      <label className="flex h-28 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50/50 text-brand-700 font-bold hover:bg-brand-50">
        {scan.isPending ? (
          <>
            <Loader2 className="animate-spin" size={22} />
            <span className="text-sm">La IA está leyendo la factura…</span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-2"><UploadCloud size={20} /> Subir factura de proveedor</span>
            <span className="text-xs font-semibold text-brand-600/70 inline-flex items-center gap-1">
              <Sparkles size={12} /> PDF o foto — extracción automática con IA
            </span>
          </>
        )}
        <input
          type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp"
          onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }}
          disabled={scan.isPending}
        />
      </label>
      {scanError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800">
          {scanError} — puedes rellenar los campos a mano.
        </div>
      )}

      {/* Filtro + listado */}
      <select className="w-52 rounded-lg border border-slate-300 px-3 py-2 text-sm" value={estado} onChange={(e) => setEstado(e.target.value)}>
        <option value="">Todas</option>
        <option value="pendiente">Pendientes de pago</option>
        <option value="pagada">Pagadas</option>
      </select>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>
      ) : (
        <div className="rounded-2xl border border-slate-200/70 bg-white overflow-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="text-left text-xs font-black uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3">Proveedor</th>
                <th className="px-4 py-3">Nº factura</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Vencimiento</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Pago</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((c) => (
                <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-bold text-slate-800">
                    {c.proveedor?.nombre ?? "—"}
                    {c.proveedor?.cif && <span className="ml-2 text-xs text-slate-400">{c.proveedor.cif}</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-600">{c.numero_factura ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.fecha ? new Date(c.fecha).toLocaleDateString("es-ES") : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.vencimiento ? new Date(c.vencimiento).toLocaleDateString("es-ES") : "—"}</td>
                  <td className="px-4 py-3 text-right font-black text-slate-900">{eur(Number(c.total))}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${c.estado_pago === "pagada" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {c.estado_pago}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      {c.archivo && (
                        <button title="Ver archivo" onClick={() => void downloadCompra(c.id)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><FileDown size={16} /></button>
                      )}
                      {c.estado_pago === "pendiente" && (
                        <button title="Marcar pagada" onClick={() => marcar.mutate({ id: c.id, payload: { estado_pago: "pagada" } })} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"><CheckCircle2 size={16} /></button>
                      )}
                      <button title="Eliminar" onClick={() => { if (confirm("¿Eliminar esta factura recibida?")) eliminar.mutate(c.id); }} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!data?.data.length && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Sin facturas de proveedor. Sube la primera.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {draft && <ReviewModal draft={draft} onClose={() => setDraft(null)} />}
    </div>
  );
}

/* ── Modal de revisión (borrador OCR → registrar) ─────── */

function ReviewModal({ draft, onClose }: { draft: { scan: ScanResult; campos: CompraOcrCampos }; onClose: () => void }) {
  const create = useCreateCompra();
  const { data: emisores } = useIssuers();
  const [f, setF] = useState({
    proveedor_nombre: draft.campos.proveedor_nombre ?? "",
    proveedor_cif: draft.campos.proveedor_cif ?? "",
    emisor_id: "",
    numero_factura: draft.campos.numero_factura ?? "",
    fecha: draft.campos.fecha ?? "",
    vencimiento: draft.campos.vencimiento ?? "",
    base: draft.campos.base ?? 0,
    iva_importe: draft.campos.iva_importe ?? 0,
    total: draft.campos.total ?? 0,
    categoria: "",
  });
  const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";
  const set = (k: keyof typeof f, v: string | number) => setF((s) => ({ ...s, [k]: v }));

  const [saveError, setSaveError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!f.proveedor_nombre.trim()) return;
    setSaveError(null);
    try {
      await create.mutateAsync({
        proveedor_nombre: f.proveedor_nombre.trim(),
        proveedor_cif: f.proveedor_cif || null,
        emisor_id: f.emisor_id ? Number(f.emisor_id) : null,
        numero_factura: f.numero_factura || null,
        fecha: f.fecha || null,
        vencimiento: f.vencimiento || null,
        base: Number(f.base),
        iva_importe: Number(f.iva_importe),
        total: Number(f.total),
        categoria: f.categoria || null,
        archivo: draft.scan.archivo,
        archivo_disk: draft.scan.archivo_disk,
        datos_ocr: draft.campos as unknown as Record<string, unknown>,
      });
      onClose();
    } catch (err) {
      const resp = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response?.data;
      const detalle = resp?.errors ? Object.values(resp.errors).flat().join(" · ") : resp?.message;
      setSaveError(detalle ?? "No se pudo guardar. ¿Está el servidor levantado?");
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-4 backdrop-blur-sm overflow-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl flex flex-col gap-4 my-8">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-slate-900 inline-flex items-center gap-2">
            <Sparkles size={18} className="text-brand-700" /> Revisar datos extraídos
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-500 -mt-2">La IA ha rellenado los campos. Corrige lo que haga falta y registra.</p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="text-xs font-bold text-slate-500 uppercase">Proveedor *</span>
            <input className={input} value={f.proveedor_nombre} onChange={(e) => set("proveedor_nombre", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">CIF</span>
            <input className={input} value={f.proveedor_cif} onChange={(e) => set("proveedor_cif", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Nº factura</span>
            <input className={input} value={f.numero_factura} onChange={(e) => set("numero_factura", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Fecha</span>
            <input type="date" className={input} value={f.fecha} onChange={(e) => set("fecha", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Vencimiento</span>
            <input type="date" className={input} value={f.vencimiento} onChange={(e) => set("vencimiento", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Base (€)</span>
            <input type="number" step="0.01" className={input} value={f.base} onChange={(e) => set("base", Number(e.target.value))} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">IVA (€)</span>
            <input type="number" step="0.01" className={input} value={f.iva_importe} onChange={(e) => set("iva_importe", Number(e.target.value))} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Total (€)</span>
            <input type="number" step="0.01" className={input} value={f.total} onChange={(e) => set("total", Number(e.target.value))} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Empresa receptora</span>
            <select className={input} value={f.emisor_id} onChange={(e) => set("emisor_id", e.target.value)}>
              <option value="">—</option>
              {emisores?.map((em) => <option key={em.id} value={em.id}>{em.nombre}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase">Categoría</span>
            <input className={input} placeholder="Material, software, suministros…" value={f.categoria} onChange={(e) => set("categoria", e.target.value)} />
          </label>
        </div>

        {saveError && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700">
            {saveError}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={create.isPending || !f.proveedor_nombre.trim()} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
            {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Registrar compra
          </button>
        </div>
      </form>
    </div>
  );
}
